/**
 * 2026-05-04 — login redirect helper.
 *
 * Founder direction: when an authed user hits an authed-only page
 * unauth'd, the redirect to /login should round-trip back to the
 * page they tried to reach — not always to /home / /dashboard.
 * "Common sense industry knowledge: a user on /feed/posts shouldn't
 * have to click through /home before getting back."
 *
 * Usage:
 *   import { loginUrl } from "@/lib/loginUrl";
 *   router.replace(loginUrl());
 *
 * Generic pages (`/`, `/login`, `/signup`, `/forgot-password`,
 * `/reset-password`) intentionally fall back to plain `/login`
 * since rounding-trip-on-the-landing-page makes no sense.
 *
 * SSR-safe: returns plain `/login` on the server (no window).
 */
const GENERIC_PATHS = new Set([
  "/",
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
]);

export function loginUrl(explicitPath?: string): string {
  if (typeof window === "undefined") return "/login";
  const raw =
    explicitPath ?? window.location.pathname + window.location.search;
  // Strip any `?next=` already present to avoid nested encoding loops.
  const path = raw.split("?")[0] ?? raw;
  if (GENERIC_PATHS.has(path)) return "/login";
  // Preserve querystring when it exists — e.g. /markets?cat=x should
  // round-trip with the filter applied.
  return `/login?next=${encodeURIComponent(raw)}`;
}
